import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { COLORS } from "../../../constants";

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function formatISODateToBR(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function fileDateSuffix(): string {
  return `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;
}

function tipoLabel(tipo: string | null): string {
  if (tipo === "doce") return "Doce";
  if (tipo === "salgada") return "Salgada";
  return "—";
}

export type RelatorioSalaoPizzasFiltroResumo = {
  label: string;
  value: string;
};

export type RelatorioSalaoPizzasExportSecao = {
  titulo: string;
  totalPizzas: number;
  porTamanho: Array<{ tamanhoNome: string; quantidadePizzas: number }>;
  linhas: Array<{
    saborNome: string;
    tamanhoNome: string;
    tipo: string | null;
    quantidadePizzas: number;
  }>;
};

export interface RelatorioSalaoPizzasExportInput {
  periodoInicioISO: string;
  periodoFimISO: string;
  filtros: RelatorioSalaoPizzasFiltroResumo[];
  secoes: RelatorioSalaoPizzasExportSecao[];
  totalPizzas: number;
  geradoEmLabel: string;
}

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

export function exportRelatorioSalaoPizzasXlsx(
  input: RelatorioSalaoPizzasExportInput
): void {
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);
  const rows: (string | number)[][] = [
    ["Dom Belloni"],
    ["Relatório — Pizzas Salão (integração)"],
    [],
    ["Período", `${pIni} a ${pFim}`],
    ...input.filtros.map((f) => [f.label, f.value]),
    [],
  ];

  for (const secao of input.secoes) {
    rows.push([secao.titulo], [`Total da seção`, secao.totalPizzas], []);
    rows.push(["Por tamanho"], ["Tamanho", "Qtd. pizzas"]);
    for (const row of secao.porTamanho) {
      rows.push([row.tamanhoNome, row.quantidadePizzas]);
    }
    rows.push(["Total", secao.totalPizzas], []);
    rows.push(
      ["Por sabor e tamanho"],
      ["Sabor", "Tamanho", "Tipo", "Qtd. pizzas"]
    );
    for (const row of secao.linhas) {
      rows.push([
        row.saborNome,
        row.tamanhoNome,
        tipoLabel(row.tipo),
        row.quantidadePizzas,
      ]);
    }
    rows.push(["Total", "", "", secao.totalPizzas], []);
  }

  rows.push(
    ["Total geral (salão + rodízio)", input.totalPizzas],
    [],
    ["Gerado em", input.geradoEmLabel]
  );

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 28 }, { wch: 20 }, { wch: 12 }, { wch: 12 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Relatório");
  XLSX.writeFile(wb, `relatorio-salao-pizzas-${fileDateSuffix()}.xlsx`);
}

export function exportRelatorioSalaoPizzasPdf(
  input: RelatorioSalaoPizzasExportInput
): void {
  const primary = hexToRgb(COLORS.PRIMARY);
  const primaryDark = hexToRgb(COLORS.PRIMARY_DARK);
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const docExt = doc as JsPdfWithAutoTable;

  doc.setFillColor(primary[0], primary[1], primary[2]);
  doc.rect(0, 0, pageW, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Pizzas Salão", margin, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Dom Belloni · Pedidos integração", margin, 19);

  autoTable(doc, {
    startY: 32,
    body: [
      ["Período", `${pIni}  até  ${pFim}`],
      ...input.filtros.map((f) => [f.label, f.value]),
      ["Total geral", String(input.totalPizzas)],
    ],
    theme: "plain",
    styles: { fontSize: 9, textColor: [42, 42, 42], cellPadding: 2.5 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 36 },
      1: { cellWidth: pageW - margin * 2 - 36 },
    },
    margin: { left: margin, right: margin },
  });

  let cursorY = (docExt.lastAutoTable?.finalY ?? 40) + 6;

  for (const secao of input.secoes) {
    if (cursorY > pageH - 40) {
      doc.addPage();
      cursorY = margin;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(38, 38, 38);
    doc.text(
      `${secao.titulo}  ·  Total: ${secao.totalPizzas}`,
      margin,
      cursorY
    );
    cursorY += 5;

    autoTable(doc, {
      startY: cursorY,
      head: [["Tamanho", "Qtd. pizzas"]],
      body: secao.porTamanho.map((r) => [
        r.tamanhoNome,
        String(r.quantidadePizzas),
      ]),
      foot: [["Total", String(secao.totalPizzas)]],
      theme: "striped",
      headStyles: {
        fillColor: primary,
        textColor: 255,
        fontStyle: "bold",
      },
      footStyles: {
        fillColor: primaryDark,
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: pageW - margin * 2 - 28, halign: "left" },
        1: { cellWidth: 28, halign: "right" },
      },
      bodyStyles: { textColor: [38, 38, 38], lineColor: [230, 230, 230] },
      alternateRowStyles: { fillColor: [255, 252, 251] },
      margin: { left: margin, right: margin, bottom: 16 },
      styles: {
        fontSize: 8,
        cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
        lineWidth: 0.08,
      },
    });

    cursorY = (docExt.lastAutoTable?.finalY ?? cursorY) + 6;

    autoTable(doc, {
      startY: cursorY,
      head: [["Sabor", "Tamanho", "Tipo", "Qtd. pizzas"]],
      body: secao.linhas.map((r) => [
        r.saborNome,
        r.tamanhoNome,
        tipoLabel(r.tipo),
        String(r.quantidadePizzas),
      ]),
      foot: [["Total", "", "", String(secao.totalPizzas)]],
      theme: "striped",
      headStyles: {
        fillColor: primary,
        textColor: 255,
        fontStyle: "bold",
      },
      footStyles: {
        fillColor: primaryDark,
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: pageW - margin * 2 - 28 - 28 - 22, halign: "left" },
        1: { cellWidth: 28, halign: "left" },
        2: { cellWidth: 22, halign: "left" },
        3: { cellWidth: 28, halign: "right" },
      },
      bodyStyles: { textColor: [38, 38, 38], lineColor: [230, 230, 230] },
      alternateRowStyles: { fillColor: [255, 252, 251] },
      margin: { left: margin, right: margin, bottom: 16 },
      styles: {
        fontSize: 8,
        cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
        lineWidth: 0.08,
      },
    });

    cursorY = (docExt.lastAutoTable?.finalY ?? cursorY) + 8;
  }

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(115, 115, 115);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${input.geradoEmLabel}  ·  Folha ${i} de ${pages}`,
      margin,
      pageH - 6
    );
  }

  doc.save(`relatorio-salao-pizzas-${fileDateSuffix()}.pdf`);
}
